// keyrd_console/src/components/PatientProfile.js
import React, { useEffect, useState } from "react";
import NcptDiagnosisSelector from "./NcptDiagnosisSelector";
import { buildKeyrdHeaders } from "../api/headers";

// ---------------------------------------------------------------------
// Option sets for standardized fields
// ---------------------------------------------------------------------
const SEX_OPTIONS = [
  "",
  "Female",
  "Male",
  "Non-binary",
  "Intersex",
  "Another / specify in notes",
  "Prefer not to say",
];

const RACE_ETHNICITY_OPTIONS = [
  "",
  "Black / African American",
  "Hispanic / Latino",
  "White",
  "Asian",
  "Native American / Alaska Native",
  "Native Hawaiian / Pacific Islander",
  "Mixed",
  "Another / specify in notes",
  "Prefer not to say",
];

const RELIGION_OPTIONS = [
  "",
  "None",
  "Christian",
  "Muslim",
  "Jewish",
  "Hindu",
  "Buddhist",
  "Sikh",
  "Another / specify in notes",
  "Prefer not to say",
];

const DIET_PATTERN_OPTIONS = [
  "",
  "Omnivore",
  "Mediterranean",
  "Vegetarian",
  "Vegan",
  "Pescatarian",
  "DASH",
  "Low FODMAP",
  "Keto / very low carb",
  "High protein",
  "Another / mixed pattern",
];

const BUDGET_OPTIONS = [
  "",
  "Very tight",
  "Tight",
  "Moderate",
  "Comfortable",
  "Unknown / variable",
];

const WORK_TYPE_OPTIONS = [
  "",
  "Student",
  "Desk / office",
  "Manual / shift",
  "Healthcare",
  "Hospitality / service",
  "Self-employed",
  "Unemployed",
  "Retired",
  "Primary caregiver",
  "Another / mixed",
];

const SCHEDULE_OPTIONS = [
  "",
  "Standard daytime",
  "Evening shifts",
  "Night shifts",
  "Rotating shifts",
  "Irregular / gig work",
];

const SLEEP_PATTERN_OPTIONS = [
  "",
  "Consistent 7–9 hours",
  "Short sleep <6h",
  "Long sleep >9h",
  "Fragmented / insomnia",
  "Rotating / jet-lagged",
];

const DEVICE_TYPE_OPTIONS = [
  "",
  "iOS",
  "Android",
  "Shared device",
  "Limited / intermittent access",
];

const NOTIFICATION_WINDOW_OPTIONS = [
  "",
  "Mornings",
  "Evenings",
  "Mornings & evenings",
  "Work breaks only",
  "Weekdays only",
  "Weekends only",
];

const ED_RISK_OPTIONS = [
  "",
  "No known risk",
  "Past history – in remission",
  "Current concern",
  "High concern – restrict weight language",
];

const RISK_FLAGS_OPTIONS = [
  "",
  "Pregnancy",
  "Type 1 diabetes",
  "Type 2 diabetes on insulin",
  "CKD (stage 3+)",
  "CVD / heart failure",
  "Food allergy anaphylaxis risk",
  "Another high-risk condition",
];

const CONTRAINDICATION_OPTIONS = [
  "",
  "No intermittent fasting",
  "No weight-loss framing",
  "No unsupervised exercise nudges",
  "No sodium restriction",
  "Other – specify in notes",
];

// ---------------------------------------------------------------------
// Canonical empty profile + hydrator
// ---------------------------------------------------------------------
const EMPTY_PROFILE = {
  identity: {
    sex: "",
    age: "",
    race_ethnicity: "",
    language: "",
    religion: "",
  },
  diet: {
    pattern: "",
    restrictions: "",
    allergies: "",
    dislikes: "",
    cultural_notes: "",
  },
  environment: {
    food_access: "",
    budget: "",
    kitchen: "",
    transportation: "",
    financial_constraints: "",
  },
  work_sleep_device: {
    work_type: "",
    schedule: "",
    sleep_pattern: "",
    device_type: "",
    notification_windows: "",
  },
  goals: {
    rdn_goals: "",
    patient_goals: "",
    motivation_notes: "",
  },
  safety: {
    ed_risk: "",
    high_risk_flags: "",
    contraindications: "",
  },
  notes: "",
};

function hydrateProfile(source) {
  const src = source && typeof source === "object" ? source : {};
  return {
    ...EMPTY_PROFILE,
    ...src,
    identity: {
      ...EMPTY_PROFILE.identity,
      ...(src.identity || {}),
    },
    diet: {
      ...EMPTY_PROFILE.diet,
      ...(src.diet || {}),
    },
    environment: {
      ...EMPTY_PROFILE.environment,
      ...(src.environment || {}),
    },
    work_sleep_device: {
      ...EMPTY_PROFILE.work_sleep_device,
      ...(src.work_sleep_device || {}),
    },
    goals: {
      ...EMPTY_PROFILE.goals,
      ...(src.goals || {}),
    },
    safety: {
      ...EMPTY_PROFILE.safety,
      ...(src.safety || {}),
    },
  };
}

// ---------------------------------------------------------------------
// Small helper components
// ---------------------------------------------------------------------
function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: "4px 6px",
        borderRadius: 4,
        border: "1px solid #d1d5db",
        fontSize: 12,
      }}
    >
      <option value="">{placeholder || "Select…"}</option>
      {options
        .filter((opt) => opt !== "")
        .map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
    </select>
  );
}

// Tiny inline line chart for reward trends
function Sparkline({ data, width = 120, height = 30 }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const numeric = data
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));

  if (numeric.length === 0) return null;

  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  const span = max - min || 1;

  const points = numeric
    .map((v, i) => {
      const x =
        numeric.length === 1 ? width / 2 : (i / (numeric.length - 1)) * width;
      const y = height - ((v - min) / span) * height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      style={{ display: "block" }}
    >
      <polyline
        fill="none"
        stroke="#6b7280"
        strokeWidth="1.5"
        points={points}
      />
    </svg>
  );
}

// Tiny inline bar chart for time-of-day response density
function BarSparkline({ data, width = 120, height = 30 }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const numeric = data
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));

  if (numeric.length === 0) return null;

  const max = Math.max(...numeric) || 1;
  const barWidth = width / numeric.length;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      style={{ display: "block" }}
    >
      {numeric.map((v, i) => {
        const barHeight = (v / max) * height;
        const x = i * barWidth;
        const y = height - barHeight;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={Math.max(barWidth - 1, 1)}
            height={barHeight}
            fill="#9ca3af"
          />
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------
function PatientProfile({ auth, patientId }) {
  const { baseUrl, token, authorized } = auth;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  const [editingProfile, setEditingProfile] = useState(false);
  const [draftProfile, setDraftProfile] = useState(() =>
    hydrateProfile(null)
  );
  const [savingProfile, setSavingProfile] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [showFamilyMapping, setShowFamilyMapping] = useState(false);

  // Insight Engine client state
  const [insightSummary, setInsightSummary] = useState(null);
  const [insightError, setInsightError] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // -------------------------------------------------------------------
  // Load summary + insights
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!authorized || !patientId || !baseUrl) {
      setData(null);
      setErr(null);
      setInsightSummary(null);
      setInsightError(null);
      setInsightsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchSummaryAndInsights() {
      setLoading(true);
      setErr(null);
      setInsightError(null);
      setInsightsLoading(false);

      try {
        // 1) Core patient summary (existing behavior)
        const resp = await fetch(
          `${baseUrl}/console/patients/${patientId}/summary`,
          {
            headers: buildKeyrdHeaders({ token }),
          }
        );

        if (!resp.ok) {
          const text = await resp.text();
          if (!cancelled) {
            setErr(
              `Error loading patient summary (${resp.status}): ${
                text || "Unknown error"
              }`
            );
            setData(null);
          }
          return;
        }

        const json = await resp.json();
        if (!cancelled) {
          if (json && json.ok) {
            setData(json);
            if (!editingProfile) {
              setDraftProfile(hydrateProfile(json.profile));
            }
          } else {
            setErr("Unexpected response from server while loading patient.");
            setData(null);
          }
        }

        // 2) Behavioral insights from logs/history
        if (cancelled) return;

        setInsightsLoading(true);

        try {
          const insightsResp = await fetch(
            `${baseUrl}/api/logs/history?patient_id=${encodeURIComponent(
              patientId
            )}`,
            {
              headers: buildKeyrdHeaders({ token }),
            }
          );

          if (!insightsResp.ok) {
            const text = await insightsResp.text();
            if (!cancelled) {
              setInsightError(
                `Error loading insights (${insightsResp.status}): ${
                  text || "Unknown error"
                }`
              );
              setInsightSummary(null);
            }
            return;
          }

          const insightsJson = await insightsResp.json();
          if (!cancelled) {
            if (insightsJson && insightsJson.ok) {
              const summary =
                insightsJson.insight_summary ||
                insightsJson.insights ||
                null;
              setInsightSummary(summary);
              setInsightError(null);
            } else {
              setInsightSummary(null);
              setInsightError(
                "Unexpected response from server while loading insights."
              );
            }
          }
        } catch (e) {
          if (!cancelled) {
            setInsightSummary(null);
            setInsightError(`Network error loading insights: ${e}`);
          }
        } finally {
          if (!cancelled) {
            setInsightsLoading(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErr(`Network error loading patient: ${e}`);
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchSummaryAndInsights();

    return () => {
      cancelled = true;
    };
  }, [authorized, baseUrl, patientId, token, editingProfile]);

  if (!authorized) {
    return (
      <div style={{ padding: 16, fontSize: 14 }}>
        Sign in to the console to view patient profiles.
      </div>
    );
  }

  if (!patientId) {
    return (
      <div style={{ padding: 16, fontSize: 14 }}>
        Select a patient from the list to view details.
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div style={{ padding: 16, fontSize: 14 }}>
        Loading patient profile...
      </div>
    );
  }

  if (err && !data) {
    return (
      <div style={{ padding: 16, fontSize: 14, color: "#b91c1c" }}>
        {String(err)}
      </div>
    );
  }

  const patient = data && data.patient ? data.patient : null;
  const diagnosesSummary =
    data && data.diagnoses_summary ? data.diagnoses_summary : null;
  const profile = hydrateProfile(data && data.profile);

  // Prefer insight_summary from summary payload if it ever exists
  const effectiveInsights =
    (data && data.insight_summary) || insightSummary || null;

  // -------------------------------------------------------------------
  // NCPT family helpers
  // -------------------------------------------------------------------
  function getFamilyKeysForDiagnosis(d, summary) {
    if (!d) return [];

    if (Array.isArray(d.family_keys)) return d.family_keys;
    if (Array.isArray(d.family_key_list)) return d.family_key_list;
    if (
      summary &&
      summary.family_by_code &&
      d.code &&
      Array.isArray(summary.family_by_code[d.code])
    ) {
      return summary.family_by_code[d.code];
    }
    return [];
  }

  function computeDerivedFamilyKeys(summary) {
    if (!summary) return [];
    let keys = [];

    if (Array.isArray(summary.ncpt_family_keys)) {
      keys = keys.concat(summary.ncpt_family_keys);
    }
    if (Array.isArray(summary.family_keys)) {
      keys = keys.concat(summary.family_keys);
    }
    if (Array.isArray(summary.all_diagnoses)) {
      for (const d of summary.all_diagnoses) {
        const fams = getFamilyKeysForDiagnosis(d, summary);
        if (Array.isArray(fams)) keys = keys.concat(fams);
      }
    }

    const unique = Array.from(new Set(keys.filter(Boolean)));
    unique.sort();
    return unique;
  }

  const derivedFamilyKeys = computeDerivedFamilyKeys(diagnosesSummary);
  const hasFamilyKeys =
    Array.isArray(derivedFamilyKeys) && derivedFamilyKeys.length > 0;

  // -------------------------------------------------------------------
  // Profile editing handlers
  // -------------------------------------------------------------------
  const handleStartEdit = () => {
    setDraftProfile(hydrateProfile(data && data.profile));
    setSaveError(null);
    setEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setEditingProfile(false);
    setSaveError(null);
  };

  const handleProfileChange = (section, field, value) => {
    setDraftProfile((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNotesChange = (value) => {
    setDraftProfile((prev) => ({
      ...prev,
      notes: value,
    }));
  };

  const handleSaveProfile = async () => {
    if (!baseUrl || !token || !patientId) return;

    setSavingProfile(true);
    setSaveError(null);

    try {
      const resp = await fetch(
        `${baseUrl}/console/patients/${patientId}/profile`,
        {
          method: "PUT",
          headers: buildKeyrdHeaders({ token, contentType: "application/json" }),
          body: JSON.stringify({ profile: draftProfile }),
        }
      );

      let json = null;
      try {
        json = await resp.json();
      } catch {
        // tolerate empty body
      }

      if (!resp.ok || (json && json.ok === false)) {
        const msg =
          (json && json.error) ||
          `HTTP ${resp.status} while saving profile.`;
        setSaveError(msg);
        return;
      }

      setData((prev) =>
        prev
          ? {
              ...prev,
              profile: { ...(prev.profile || {}), ...draftProfile },
            }
          : prev
      );
      setEditingProfile(false);
      setSaveError(null);
    } catch (e) {
      setSaveError(`Network error while saving profile: ${e}`);
    } finally {
      setSavingProfile(false);
    }
  };

  // -------------------------------------------------------------------
  // Derived "critical gaps"
  // -------------------------------------------------------------------
  const criticalGaps = [];
  if (!profile.diet.pattern && !profile.diet.restrictions) {
    criticalGaps.push("Diet pattern / restrictions");
  }
  if (!profile.goals.patient_goals && !profile.goals.rdn_goals) {
    criticalGaps.push("Goals & motivation");
  }
  if (!profile.safety.high_risk_flags && !profile.safety.ed_risk) {
    criticalGaps.push("Safety & risk");
  }

  // -------------------------------------------------------------------
  // Insight formatting helpers
  // -------------------------------------------------------------------
  function formatPercent(value) {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return `${n.toFixed(1)}%`;
  }

  function formatMean(value) {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return n.toFixed(2);
  }

  function formatWindowLabel(window) {
    if (!window || typeof window !== "string") return "Not yet clear";
    const lower = window.toLowerCase();
    if (lower === "morning") return "Morning";
    if (lower === "afternoon") return "Afternoon";
    if (lower === "evening") return "Evening";
    if (lower === "night") return "Night";
    return window;
  }

  // -------------------------------------------------------------------
  // Rendering helpers
  // -------------------------------------------------------------------
  function renderSectionCard(title, description, lines) {
    const hasContent = lines.some((l) => l.value && l.value.trim() !== "");
    return (
      <div
        style={{
          flex: 1,
          minWidth: 260,
          borderRadius: 8,
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
          padding: 12,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 4,
            color: "#111827",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "#6b7280",
            marginBottom: 6,
          }}
        >
          {description}
        </div>
        {hasContent ? (
          <ul
            style={{
              listStyle: "none",
              paddingLeft: 0,
              margin: 0,
              fontSize: 12,
              color: "#374151",
            }}
          >
            {lines.map((line) =>
              line.value && line.value.trim() !== "" ? (
                <li key={line.label} style={{ marginBottom: 2 }}>
                  <strong>{line.label}: </strong>
                  <span>{line.value}</span>
                </li>
              ) : null
            )}
          </ul>
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "#9ca3af",
            }}
          >
            No {title.toLowerCase()} recorded yet.
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Extract and shape insights for display
  // -------------------------------------------------------------------
  const engagement = effectiveInsights && effectiveInsights.engagement
    ? effectiveInsights.engagement
    : null;
  const rewardPatterns =
    effectiveInsights && effectiveInsights.reward_patterns
      ? effectiveInsights.reward_patterns
      : null;
  const temporal =
    effectiveInsights && effectiveInsights.temporal_sensitivity
      ? effectiveInsights.temporal_sensitivity
      : null;
  const visitRecommendations =
    effectiveInsights && effectiveInsights.visit_recommendations
      ? effectiveInsights.visit_recommendations
      : null;

  const engagementTrend = engagement && engagement.trend
    ? engagement.trend
    : "unknown";
  const engagementDelta = engagement
    ? formatPercent(engagement.percent_change_14d)
    : null;

  const meanReward = rewardPatterns
    ? formatMean(rewardPatterns.overall_mean_reward)
    : null;
  const rewardN = rewardPatterns ? rewardPatterns.overall_n : null;

  const bestWindow = temporal ? formatWindowLabel(temporal.best_window) : null;

  const talkingPoints =
    visitRecommendations && Array.isArray(visitRecommendations.talking_points)
      ? visitRecommendations.talking_points
      : [];
  const planConsiderations =
    visitRecommendations &&
    Array.isArray(visitRecommendations.plan_edit_considerations)
      ? visitRecommendations.plan_edit_considerations
      : [];
  const visitSummary =
    visitRecommendations && visitRecommendations.summary
      ? visitRecommendations.summary
      : null;

  const hasAnyInsights =
    !!engagement ||
    !!rewardPatterns ||
    !!temporal ||
    !!visitRecommendations;

  // Sparkline data: reward trend (if backend provides reward_by_day)
  const rewardSparkValues =
    rewardPatterns && Array.isArray(rewardPatterns.reward_by_day)
      ? rewardPatterns.reward_by_day
          .map((d) =>
            d && typeof d.mean_reward === "number"
              ? d.mean_reward
              : Number(d && d.value)
          )
          .filter((v) => Number.isFinite(v))
      : null;

  // Bar sparkline data: responses by hour (if provided)
  const responsesByHour =
    temporal && Array.isArray(temporal.responses_by_hour)
      ? temporal.responses_by_hour
      : null;
  const responseCountValues = responsesByHour
    ? responsesByHour
        .map((b) =>
          b && typeof b.count === "number" ? b.count : Number(b && b.value)
        )
        .filter((v) => Number.isFinite(v))
    : null;

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  return (
    <div
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        position: "relative",
      }}
    >
      {/* Top overview bar */}
      <div
        style={{
          borderRadius: 8,
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
          padding: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Patient overview
          </div>
          {patient ? (
            <div style={{ fontSize: 12, color: "#374151" }}>
              <span style={{ marginRight: 12 }}>
                <strong>ID:</strong> {patient.id}
              </span>
              {patient.active !== undefined && (
                <span style={{ marginRight: 12 }}>
                  <strong>Status:</strong>{" "}
                  {patient.active ? "Active" : "Inactive"}
                </span>
              )}
              {patient.created_at && (
                <span>
                  <strong>Enrolled:</strong> {patient.created_at}
                </span>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Patient record not available.
            </div>
          )}
          {criticalGaps.length > 0 && (
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: "#92400e",
              }}
            >
              Critical gaps: {criticalGaps.join(" • ")}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {err && data && (
            <span
              style={{
                fontSize: 11,
                color: "#b91c1c",
              }}
            >
              {String(err)}
            </span>
          )}
          <button
            type="button"
            onClick={handleStartEdit}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "1px solid #0b3d2e",
              backgroundColor: "#0b3d2e",
              color: "#ffffff",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Edit profile
          </button>
        </div>
      </div>

      {/* Context cards row 1 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {renderSectionCard(
          "Identity & demographics",
          "Core demographic context used for risk assessment, cultural framing, and population analyses.",
          [
            { label: "Sex", value: profile.identity.sex },
            { label: "Age", value: profile.identity.age },
            { label: "Race / ethnicity", value: profile.identity.race_ethnicity },
            { label: "Language", value: profile.identity.language },
            { label: "Religion", value: profile.identity.religion },
          ]
        )}
        {renderSectionCard(
          "Diet pattern & restrictions",
          "High-impact diet constraints and preferences that must shape all nudges.",
          [
            { label: "Pattern", value: profile.diet.pattern },
            { label: "Restrictions", value: profile.diet.restrictions },
            { label: "Allergies", value: profile.diet.allergies },
            { label: "Dislikes", value: profile.diet.dislikes },
            { label: "Cultural notes", value: profile.diet.cultural_notes },
          ]
        )}
      </div>

      {/* Context cards row 2 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {renderSectionCard(
          "Access & environment",
          "Food access, budget, and kitchen constraints that shape what is realistically achievable.",
          [
            { label: "Food access", value: profile.environment.food_access },
            { label: "Budget", value: profile.environment.budget },
            { label: "Kitchen", value: profile.environment.kitchen },
            {
              label: "Transportation",
              value: profile.environment.transportation,
            },
            {
              label: "Financial constraints",
              value: profile.environment.financial_constraints,
            },
          ]
        )}
        {renderSectionCard(
          "Work, sleep & device context",
          "When and how KeyRD can realistically nudge this patient.",
          [
            { label: "Work type", value: profile.work_sleep_device.work_type },
            { label: "Schedule", value: profile.work_sleep_device.schedule },
            {
              label: "Sleep pattern",
              value: profile.work_sleep_device.sleep_pattern,
            },
            {
              label: "Device type",
              value: profile.work_sleep_device.device_type },
            {
              label: "Notification windows",
              value: profile.work_sleep_device.notification_windows,
            },
          ]
        )}
      </div>

      {/* Context cards row 3 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {renderSectionCard(
          "Goals & motivation",
          "RDN- and patient-driven goals that the nudge plan should move toward.",
          [
            { label: "Patient goals", value: profile.goals.patient_goals },
            { label: "RDN goals", value: profile.goals.rdn_goals },
            {
              label: "Motivation notes",
              value: profile.goals.motivation_notes,
            },
          ]
        )}
        {renderSectionCard(
          "Safety & risk",
          "Flags that constrain which nudges are allowed and how they are framed.",
          [
            { label: "ED risk", value: profile.safety.ed_risk },
            {
              label: "High-risk flags",
              value: profile.safety.high_risk_flags,
            },
            {
              label: "Contraindications",
              value: profile.safety.contraindications,
            },
          ]
        )}
      </div>

      {/* NCPT diagnoses */}
      <div
        style={{
          borderRadius: 8,
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
          padding: 12,
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: 8,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          NCPT diagnoses for this patient
        </h3>

        <NcptDiagnosisSelector
          apiBaseUrl={baseUrl}
          token={token}
          patientId={patientId}
        />
      </div>

      {/* Derived NCPT families */}
      {diagnosesSummary && (
        <div
          style={{
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            padding: 12,
          }}
        >
          <h3
            style={{
              marginTop: 0,
              marginBottom: 8,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            NCPT nudge families (derived)
          </h3>
          <p
            style={{
              marginTop: 0,
              marginBottom: 8,
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            These family keys summarize this patient&apos;s NCPT state and feed
            into nudge recommendations and controller logic.
          </p>

          {hasFamilyKeys ? (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  marginBottom: 8,
                }}
              >
                {derivedFamilyKeys.map((key) => (
                  <span
                    key={key}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "2px 8px",
                      borderRadius: 9999,
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      fontSize: 11,
                      color: "#111827",
                    }}
                  >
                    {key}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowFamilyMapping((prev) => !prev)}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  padding: "4px 8px",
                  fontSize: 11,
                  backgroundColor: "#f3f4f6",
                  cursor: "pointer",
                }}
              >
                {showFamilyMapping
                  ? "Hide code → family mapping"
                  : "Show code → family mapping"}
              </button>
              {showFamilyMapping &&
                Array.isArray(diagnosesSummary.all_diagnoses) &&
                diagnosesSummary.all_diagnoses.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      maxHeight: 200,
                      overflowY: "auto",
                      borderTop: "1px solid #e5e7eb",
                      paddingTop: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        marginBottom: 4,
                        color: "#374151",
                      }}
                    >
                      Code → family mapping
                    </div>
                    <ul
                      style={{
                        listStyle: "none",
                        paddingLeft: 0,
                        margin: 0,
                      }}
                    >
                      {diagnosesSummary.all_diagnoses.map((d) => {
                        const fams = getFamilyKeysForDiagnosis(
                          d,
                          diagnosesSummary
                        );
                        const label =
                          Array.isArray(fams) && fams.length > 0
                            ? fams.join(", ")
                            : "—";
                        return (
                          <li
                            key={
                              d.code ||
                              `${d.diagnosis_id || ""}_${d.code || ""}`
                            }
                            style={{
                              fontSize: 11,
                              color: "#4b5563",
                              marginBottom: 4,
                            }}
                          >
                            <strong>{d.code}</strong> – {d.label}{" "}
                            <span style={{ color: "#9ca3af" }}>({label})</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
            </>
          ) : (
            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              No NCPT nudge families are currently derived for this patient. Add
              diagnoses above to populate family keys.
            </div>
          )}
        </div>
      )}

      {/* Behavioral insights panel */}
      <div
        style={{
          borderRadius: 8,
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
          padding: 12,
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: 4,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Behavioral insights (KeyRD)
        </h3>
        <p
          style={{
            marginTop: 0,
            marginBottom: 8,
            fontSize: 12,
            color: "#6b7280",
          }}
        >
          High-level trends from this patient&apos;s nudge and feedback history
          to help focus your next visit.
        </p>

        {insightsLoading && !effectiveInsights && (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 4,
            }}
          >
            Loading insights...
          </div>
        )}

        {insightError && (
          <div
            style={{
              fontSize: 11,
              color: "#b91c1c",
              marginBottom: 4,
            }}
          >
            {insightError}
          </div>
        )}

        {hasAnyInsights ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              fontSize: 12,
              color: "#374151",
            }}
          >
            {/* Engagement & reward */}
            <div
              style={{
                flex: 1,
                minWidth: 220,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Engagement & reward
              </div>
              <ul
                style={{
                  listStyle: "disc",
                  paddingLeft: 16,
                  margin: 0,
                }}
              >
                <li>
                  Trend:{" "}
                  {engagementTrend === "improving"
                    ? "improving"
                    : engagementTrend === "declining"
                    ? "declining"
                    : "not yet clear"}
                  {engagementDelta ? ` (${engagementDelta} vs prior 14 days)` : ""}
                </li>
                {meanReward !== null && rewardN !== null && (
                  <li>
                    Average feedback reward: {meanReward} across {rewardN}{" "}
                    nudges with responses.
                  </li>
                )}
              </ul>
              {Array.isArray(rewardSparkValues) &&
                rewardSparkValues.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    <Sparkline data={rewardSparkValues} />
                    <div
                      style={{
                        fontSize: 10,
                        color: "#9ca3af",
                        marginTop: 2,
                      }}
                    >
                      Recent reward trend
                    </div>
                  </div>
                )}
            </div>

            {/* Timing */}
            <div
              style={{
                flex: 1,
                minWidth: 220,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Timing sensitivity
              </div>
              <ul
                style={{
                  listStyle: "disc",
                  paddingLeft: 16,
                  margin: 0,
                }}
              >
                <li>
                  Best-performing window:{" "}
                  {bestWindow || "Not yet clear from history."}
                </li>
              </ul>
              {Array.isArray(responseCountValues) &&
                responseCountValues.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    <BarSparkline data={responseCountValues} />
                    <div
                      style={{
                        fontSize: 10,
                        color: "#9ca3af",
                        marginTop: 2,
                      }}
                    >
                      Response density by time-of-day
                    </div>
                  </div>
                )}
            </div>

            {/* Next visit prep */}
            <div
              style={{
                flexBasis: "100%",
                marginTop: 4,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Next visit preparation
              </div>
              {visitSummary && (
                <p
                  style={{
                    margin: 0,
                    marginBottom: 4,
                    fontSize: 12,
                    color: "#374151",
                  }}
                >
                  {visitSummary}
                </p>
              )}
              {(talkingPoints.length > 0 || planConsiderations.length > 0) && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                  }}
                >
                  {talkingPoints.length > 0 && (
                    <div
                      style={{
                        flex: 1,
                        minWidth: 220,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 2,
                        }}
                      >
                        Talking points
                      </div>
                      <ul
                        style={{
                          listStyle: "disc",
                          paddingLeft: 16,
                          margin: 0,
                        }}
                      >
                        {talkingPoints.map((tp, idx) => (
                          <li key={`tp-${idx}`}>{tp}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {planConsiderations.length > 0 && (
                    <div
                      style={{
                        flex: 1,
                        minWidth: 220,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 2,
                        }}
                      >
                        Plan edit considerations
                      </div>
                      <ul
                        style={{
                          listStyle: "disc",
                          paddingLeft: 16,
                          margin: 0,
                        }}
                      >
                        {planConsiderations.map((pc, idx) => (
                          <li key={`pc-${idx}`}>{pc}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            Not enough history yet to generate meaningful behavioral insights.
            Once this patient has more nudges and feedback events, KeyRD will
            surface trends here to guide your next session.
          </div>
        )}
      </div>

      {/* Side-panel editor */}
      {editingProfile && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15,23,42,0.35)",
            display: "flex",
            justifyContent: "flex-end",
            zIndex: 40,
          }}
        >
          <div
            style={{
              width: "380px",
              maxWidth: "100%",
              backgroundColor: "#ffffff",
              boxShadow: "0 10px 40px rgba(15,23,42,0.45)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Edit patient profile
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#6b7280",
                  }}
                >
                  These fields shape KeyRD&apos;s nudge selection and framing.
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelEdit}
                style={{
                  border: "none",
                  background: "none",
                  fontSize: 12,
                  color: "#6b7280",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                padding: "10px 14px",
                overflowY: "auto",
                flex: 1,
                fontSize: 12,
              }}
            >
              {/* Identity & demographics */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Identity & demographics
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <SelectInput
                    value={draftProfile.identity.sex}
                    onChange={(v) =>
                      handleProfileChange("identity", "sex", v)
                    }
                    options={SEX_OPTIONS}
                    placeholder="Sex / gender"
                  />
                  <input
                    type="text"
                    placeholder="Age (exact or range, e.g., 42 or 40–49)"
                    value={draftProfile.identity.age}
                    onChange={(e) =>
                      handleProfileChange("identity", "age", e.target.value)
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                    }}
                  />
                  <SelectInput
                    value={draftProfile.identity.race_ethnicity}
                    onChange={(v) =>
                      handleProfileChange("identity", "race_ethnicity", v)
                    }
                    options={RACE_ETHNICITY_OPTIONS}
                    placeholder="Race / ethnicity"
                  />
                  <input
                    type="text"
                    placeholder="Primary language"
                    value={draftProfile.identity.language}
                    onChange={(e) =>
                      handleProfileChange(
                        "identity",
                        "language",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                    }}
                  />
                  <SelectInput
                    value={draftProfile.identity.religion}
                    onChange={(v) =>
                      handleProfileChange("identity", "religion", v)
                    }
                    options={RELIGION_OPTIONS}
                    placeholder="Religion (if relevant to diet)"
                  />
                </div>
              </div>

              {/* Diet pattern & restrictions */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Diet pattern & restrictions
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <SelectInput
                    value={draftProfile.diet.pattern}
                    onChange={(v) =>
                      handleProfileChange("diet", "pattern", v)
                    }
                    options={DIET_PATTERN_OPTIONS}
                    placeholder="Overall diet pattern"
                  />
                  <textarea
                    rows={2}
                    placeholder="Key restrictions (e.g., gluten-free, halal, no dairy)"
                    value={draftProfile.diet.restrictions}
                    onChange={(e) =>
                      handleProfileChange(
                        "diet",
                        "restrictions",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Allergies"
                    value={draftProfile.diet.allergies}
                    onChange={(e) =>
                      handleProfileChange(
                        "diet",
                        "allergies",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Strong dislikes / aversions"
                    value={draftProfile.diet.dislikes}
                    onChange={(e) =>
                      handleProfileChange(
                        "diet",
                        "dislikes",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                    }}
                  />
                  <textarea
                    rows={2}
                    placeholder="Cultural or family context for food"
                    value={draftProfile.diet.cultural_notes}
                    onChange={(e) =>
                      handleProfileChange(
                        "diet",
                        "cultural_notes",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>

              {/* Access & environment */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Access & environment
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <textarea
                    rows={2}
                    placeholder="Food access (e.g., grocery store, campus dining, food desert)"
                    value={draftProfile.environment.food_access}
                    onChange={(e) =>
                      handleProfileChange(
                        "environment",
                        "food_access",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                  <SelectInput
                    value={draftProfile.environment.budget}
                    onChange={(v) =>
                      handleProfileChange("environment", "budget", v)
                    }
                    options={BUDGET_OPTIONS}
                    placeholder="Food budget"
                  />
                  <textarea
                    rows={2}
                    placeholder="Kitchen / equipment (e.g., dorm fridge, full kitchen)"
                    value={draftProfile.environment.kitchen}
                    onChange={(e) =>
                      handleProfileChange(
                        "environment",
                        "kitchen",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                  <textarea
                    rows={2}
                    placeholder="Transportation / shopping constraints"
                    value={draftProfile.environment.transportation}
                    onChange={(e) =>
                      handleProfileChange(
                        "environment",
                        "transportation",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                  <textarea
                    rows={2}
                    placeholder="Financial constraints to respect"
                    value={draftProfile.environment.financial_constraints}
                    onChange={(e) =>
                      handleProfileChange(
                        "environment",
                        "financial_constraints",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>

              {/* Work, sleep & device */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Work, sleep & device context
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <SelectInput
                    value={draftProfile.work_sleep_device.work_type}
                    onChange={(v) =>
                      handleProfileChange("work_sleep_device", "work_type", v)
                    }
                    options={WORK_TYPE_OPTIONS}
                    placeholder="Work type"
                  />
                  <SelectInput
                    value={draftProfile.work_sleep_device.schedule}
                    onChange={(v) =>
                      handleProfileChange("work_sleep_device", "schedule", v)
                    }
                    options={SCHEDULE_OPTIONS}
                    placeholder="Work schedule"
                  />
                  <SelectInput
                    value={draftProfile.work_sleep_device.sleep_pattern}
                    onChange={(v) =>
                      handleProfileChange(
                        "work_sleep_device",
                        "sleep_pattern",
                        v
                      )
                    }
                    options={SLEEP_PATTERN_OPTIONS}
                    placeholder="Sleep pattern"
                  />
                  <SelectInput
                    value={draftProfile.work_sleep_device.device_type}
                    onChange={(v) =>
                      handleProfileChange(
                        "work_sleep_device",
                        "device_type",
                        v
                      )
                    }
                    options={DEVICE_TYPE_OPTIONS}
                    placeholder="Device type"
                  />
                  <SelectInput
                    value={draftProfile.work_sleep_device.notification_windows}
                    onChange={(v) =>
                      handleProfileChange(
                        "work_sleep_device",
                        "notification_windows",
                        v
                      )
                    }
                    options={NOTIFICATION_WINDOW_OPTIONS}
                    placeholder="Preferred notification windows"
                  />
                </div>
              </div>

              {/* Goals & motivation */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Goals & motivation
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <textarea
                    rows={2}
                    placeholder="Patient-stated goals (their words)"
                    value={draftProfile.goals.patient_goals}
                    onChange={(e) =>
                      handleProfileChange(
                        "goals",
                        "patient_goals",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                  <textarea
                    rows={2}
                    placeholder="RDN-framed goals / outcome targets"
                    value={draftProfile.goals.rdn_goals}
                    onChange={(e) =>
                      handleProfileChange("goals", "rdn_goals", e.target.value)
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                  <textarea
                    rows={2}
                    placeholder="Motivation notes, barriers, readiness"
                    value={draftProfile.goals.motivation_notes}
                    onChange={(e) =>
                      handleProfileChange(
                        "goals",
                        "motivation_notes",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>

              {/* Safety & risk */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Safety & risk
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <SelectInput
                    value={draftProfile.safety.ed_risk}
                    onChange={(v) =>
                      handleProfileChange("safety", "ed_risk", v)
                    }
                    options={ED_RISK_OPTIONS}
                    placeholder="ED risk"
                  />
                  <SelectInput
                    value={draftProfile.safety.high_risk_flags}
                    onChange={(v) =>
                      handleProfileChange("safety", "high_risk_flags", v)
                    }
                    options={RISK_FLAGS_OPTIONS}
                    placeholder="Highest-priority risk flag"
                  />
                  <SelectInput
                    value={draftProfile.safety.contraindications}
                    onChange={(v) =>
                      handleProfileChange(
                        "safety",
                        "contraindications",
                        v
                      )
                    }
                    options={CONTRAINDICATION_OPTIONS}
                    placeholder="Biggest nudge contraindication"
                  />
                </div>
              </div>

              {/* Free-form notes */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  RDN notes (PES / SOAP scaffold)
                </div>
                <textarea
                  rows={3}
                  placeholder="Optional free-form note. Later we can structure this into PES or SOAP segments."
                  value={draftProfile.notes || ""}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  style={{
                    padding: "4px 6px",
                    borderRadius: 4,
                    border: "1px solid #d1d5db",
                    resize: "vertical",
                    fontSize: 12,
                  }}
                />
              </div>

              {saveError && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#b91c1c",
                    marginBottom: 8,
                  }}
                >
                  {saveError}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "10px 14px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={savingProfile}
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  fontSize: 12,
                  cursor: savingProfile ? "default" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid #0b3d2e",
                  backgroundColor: savingProfile ? "#6b7280" : "#0b3d2e",
                  color: "#ffffff",
                  fontSize: 12,
                  cursor: savingProfile ? "default" : "pointer",
                }}
              >
                {savingProfile ? "Saving…" : "Save profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientProfile;
